<template>
  <v-dialog
    ref="dialog"
    :value="value"
    :scrollable="true"
    persistent
    :max-width="width"
    :fullscreen="fullscreen"
    :retain-focus="false"
    :content-class="dialogContentClass"
    overlay-color="#141021"
    overlay-opacity="0.4"
    :aria-label="displayTitle"
    @input="$emit('input', $event)"
    @click:outside="$emit('outside')"
  >
    <v-form class="custom-dialog-form" @submit.prevent="$emit('submit')">
      <v-card class="custom-dialog-card" elevation="0">
        <v-card-title class="custom-dialog-title">
          <div class="custom-dialog-title__content">
            <slot name="title-content">
              <span v-if="titleIcon" class="custom-dialog-title__icon">
                <v-icon size="18" :color="titleIconColor">
                  {{ titleIcon }}
                </v-icon>
              </span>
              <div class="custom-dialog-title__text-wrap">
                <span class="custom-dialog-title__text">
                  {{ displayTitle }}
                </span>
                <span v-if="subtitle" class="custom-dialog-title__subtitle">
                  {{ displaySubtitle }}
                </span>
              </div>
            </slot>
          </div>
          <slot name="title-extra" />
          <v-spacer />
          <v-btn
            v-if="closeBtn"
            type="button"
            icon
            depressed
            class="custom-dialog-close"
            :aria-label="$t('close')"
            @click="handleClose"
          >
            <v-icon size="16">fal fa-times</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text
          class="custom-dialog-body"
          :class="{
            'custom-dialog-body--no-padding-top': !bodyPaddingTop,
            'custom-dialog-body--no-footer': !footer,
          }"
        >
          <slot name="activator"></slot>
        </v-card-text>

        <v-card-actions v-if="footer" class="custom-dialog-actions">
          <v-btn
            v-if="deleteText"
            type="button"
            depressed
            class="custom-dialog-action custom-dialog-action--delete"
            @click="$emit('delete')"
          >
            {{ deleteTextTranslate ? $t(deleteText) : deleteText }}
          </v-btn>
          <v-btn
            v-if="showCancel"
            type="button"
            outlined
            color="primary"
            depressed
            class="custom-dialog-action custom-dialog-action--cancel"
            :disabled="cancelDisabled"
            @click="handleCancel"
          >
            {{ cancelTextTranslate ? $t(cancelText) : cancelText }}
          </v-btn>
          <slot name="actions">
            <v-btn
              type="submit"
              :color="submitColor"
              dark
              depressed
              class="custom-dialog-action custom-dialog-action--submit"
              :disabled="submitDisabled"
              :loading="submitLoading"
            >
              {{ submitTextTranslate ? $t(submitText) : submitText }}
            </v-btn>
          </slot>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
export default {
  name: "CustomDialog",
  props: {
    value: {},
    title: {},
    subtitle: {
      type: String,
      default: null,
    },
    closeBtn: {
      type: Boolean,
      default: true,
    },
    footer: {
      type: Boolean,
      default: true,
    },
    width: {
      default: "600px",
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    contentClass: {
      type: String,
      default: "",
    },
    is_translate: {
      type: Boolean,
      default: true,
    },
    cancelText: {
      default: "cancel",
    },
    submitText: {
      default: "ok",
    },
    showSubmitIcon: {
      type: Boolean,
      default: true,
    },
    submitColor: {
      type: String,
      default: "primary",
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
    cancelTextTranslate: {
      type: Boolean,
      default: true,
    },
    submitTextTranslate: {
      type: Boolean,
      default: true,
    },
    deleteTextTranslate: {
      type: Boolean,
      default: true,
    },
    /** Translation key for an optional third, destructive action (e.g. "delete_note"). Emits @delete. */
    deleteText: {
      type: String,
      default: null,
    },
    bodyPaddingTop: {
      type: Boolean,
      default: true,
    },
    /** Font Awesome class, e.g. fal fa-stamp */
    titleIcon: {
      type: String,
      default: null,
    },
    titleIconColor: {
      type: String,
      default: "amber darken-2",
    },
  },
  computed: {
    dialogContentClass() {
      return ["custom-dialog-content", this.contentClass].filter(Boolean).join(" ");
    },
    displayTitle() {
      if (!this.title) return "";
      return this.is_translate ? this.$t(this.title) : this.title;
    },
    displaySubtitle() {
      if (!this.subtitle) return "";
      return this.is_translate ? this.$t(this.subtitle) : this.subtitle;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.attachKeydownListener();
          this.$nextTick(() => {
            this.focusAppropriateElement();
            // Fix for Vuetify scroll handling - ensure elements exist before Vuetify tries to access them
            // This prevents the "hasAttribute is not a function" error
            if (this.$el) {
              const dialogContent = this.$el.querySelector(".v-dialog__content");
              if (dialogContent && !dialogContent.hasAttribute) {
                // Ensure hasAttribute exists (should always exist on DOM elements, but just in case)
                if (
                  dialogContent instanceof Element &&
                  !dialogContent.hasAttribute
                ) {
                  // This shouldn't happen, but if it does, we'll add a polyfill
                  Object.defineProperty(dialogContent, "hasAttribute", {
                    value: function (attr) {
                      return this.getAttribute(attr) !== null;
                    },
                    writable: false,
                    enumerable: false,
                    configurable: false,
                  });
                }
              }
            }
          });
        } else {
          this.detachKeydownListener();
        }
      },
    },
  },
  beforeDestroy() {
    this.detachKeydownListener();
  },
  methods: {
    handleCancel() {
      if (this.cancelDisabled) return;
      this.$emit("cancel");
      this.$emit("input", false);
    },
    handleClose() {
      this.$emit("cancel");
      this.$emit("input", false);
    },
    triggerSubmit() {
      if (!this.footer) return;
      if (this.submitDisabled || this.submitLoading) return;

      const dialogEl = this.getDialogElement();
      const submitBtn = dialogEl
        ? dialogEl.querySelector(
            'button[type="submit"], .custom-dialog-action--submit'
          )
        : null;

      if (submitBtn && typeof submitBtn.click === "function") {
        submitBtn.click();
      } else {
        this.$emit("submit");
      }
    },
    getDialogElement() {
      if (
        this.$refs.dialog &&
        this.$refs.dialog.$refs &&
        this.$refs.dialog.$refs.dialog
      ) {
        return this.$refs.dialog.$refs.dialog;
      }
      if (this.$el && this.$el.querySelector) {
        return this.$el.querySelector(".v-dialog");
      }
      return null;
    },
    getDialogContentElement() {
      if (
        this.$refs.dialog &&
        this.$refs.dialog.$refs &&
        this.$refs.dialog.$refs.content
      ) {
        return this.$refs.dialog.$refs.content;
      }
      const dialogEl = this.getDialogElement();
      if (dialogEl && dialogEl.closest) {
        return dialogEl.closest(".v-dialog__content");
      }
      return null;
    },
    isTopmostDialog() {
      const myContent = this.getDialogContentElement();
      if (!myContent) return false;

      const allActive = Array.from(
        document.querySelectorAll(
          ".v-dialog__content.v-dialog__content--active"
        )
      );
      if (!allActive.length) return false;
      if (allActive.length === 1) return allActive[0] === myContent;

      let topEl = allActive[0];
      let topZ = -Infinity;
      for (const el of allActive) {
        const z = parseInt(window.getComputedStyle(el).zIndex, 10) || 0;
        if (z >= topZ) {
          topZ = z;
          topEl = el;
        }
      }
      return topEl === myContent;
    },
    focusAppropriateElement() {
      try {
        const dialogEl = this.getDialogElement();
        if (!dialogEl) return;

        // Check if there are form inputs or textareas inside dialog body
        const inputs = dialogEl.querySelectorAll(
          "input:not([type='hidden']):not([disabled]), textarea:not([disabled])"
        );
        if (inputs && inputs.length > 0) {
          return;
        }

        // Confirmation / action dialog without form inputs - focus the submit button
        if (this.footer && !this.submitDisabled && !this.submitLoading) {
          const submitBtn = dialogEl.querySelector(
            'button[type="submit"], .custom-dialog-action--submit'
          );
          if (submitBtn && typeof submitBtn.focus === "function") {
            submitBtn.focus();
          }
        }
      } catch (_) {
        // ignore
      }
    },
    onKeydown(event) {
      if (!this.value) return;

      // Ignore during IME composition (Lao/Thai/Chinese)
      if (event.isComposing || event.keyCode === 229) return;

      // Handle ESC key -> Cancel
      if (event.key === "Escape" || event.key === "Esc") {
        if (!this.isTopmostDialog()) return;

        // If an active select/autocomplete/menu is open, let menu handle Esc first
        if (document.querySelector(".v-menu__content--active")) return;
        // If SweetAlert is open on top, let SweetAlert handle Esc
        if (document.querySelector(".swal2-container.swal2-shown")) return;

        if (this.cancelDisabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.handleCancel();
        return;
      }

      // Handle Enter key -> Submit
      if (event.key === "Enter") {
        // Ignore modifier keys
        if (
          event.shiftKey ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey
        ) {
          return;
        }

        if (!this.isTopmostDialog()) return;

        // If an active select/autocomplete/menu is open, let menu handle Enter first
        if (document.querySelector(".v-menu__content--active")) return;
        // If SweetAlert is open on top, let SweetAlert handle Enter
        if (document.querySelector(".swal2-container.swal2-shown")) return;

        // In textarea or contenteditable, Enter should insert a newline
        const active = document.activeElement;
        if (
          active &&
          (active.tagName === "TEXTAREA" || active.isContentEditable)
        ) {
          return;
        }

        // If another button (e.g. Cancel or Delete) is explicitly focused, let browser activate it natively
        if (active && active.tagName === "BUTTON") {
          const dialogEl = this.getDialogElement();
          const submitBtn = dialogEl
            ? dialogEl.querySelector(
                'button[type="submit"], .custom-dialog-action--submit'
              )
            : null;
          if (active !== submitBtn) {
            return;
          }
        }

        if (!this.footer) return;
        if (this.submitDisabled || this.submitLoading) return;

        event.preventDefault();
        event.stopPropagation();
        this.triggerSubmit();
      }
    },
    attachKeydownListener() {
      if (this._hasKeydownListener) return;
      window.addEventListener("keydown", this.onKeydown);
      this._hasKeydownListener = true;
    },
    detachKeydownListener() {
      if (!this._hasKeydownListener) return;
      window.removeEventListener("keydown", this.onKeydown);
      this._hasKeydownListener = false;
    },
  },
};
</script>

<style>
/* PosSmart v2 brutal dialog shell — mirrors the sale-page note modal
   (UiModal: bg-white / brutal / rounded-3xl / shadow-hardlg) */
.v-dialog.custom-dialog-content,
.v-dialog__content.custom-dialog-content > .v-dialog {
  border-radius: 24px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
}

.v-dialog__content.custom-dialog-content {
  background: transparent !important;
  box-shadow: none !important;
}

.v-dialog.custom-dialog-content > .custom-dialog-form,
.v-dialog__content.custom-dialog-content > .v-dialog > .custom-dialog-form {
  border-radius: inherit;
  overflow: hidden;
}

@media (max-width: 600px) {
  .v-dialog.custom-dialog-content,
  .v-dialog__content.custom-dialog-content > .v-dialog {
    margin: 16px !important;
  }
}
</style>

<style scoped>
.custom-dialog-form {
  width: 100%;
}

.custom-dialog-card {
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 24px !important;
  background: #ffffff;
  box-shadow: none !important;
}

.theme--dark .custom-dialog-card {
  border-color: #3f4650;
  background: #171b21;
}

/* the global `.theme--dark.v-card` rule forces a 14px radius + thinner border; the dialog shell
   clips at 24px, so without this the card's corners and border get cut off */
.custom-dialog-card.theme--dark.v-card {
  border: 1px solid #3f4650 !important;
  border-radius: 24px !important;
  background-color: #171b21 !important;
}

/* ---- Header (title + optional subtitle + close) ---- */
.custom-dialog-title {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 24px 28px 0 !important;
  background: #ffffff;
}

.theme--dark .custom-dialog-title {
  background: #171b21;
}

.custom-dialog-title__content {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.custom-dialog-title__text-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
  padding-top: 2px;
}

.custom-dialog-title__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-top: 2px;
}

.custom-dialog-title__text {
  min-width: 0;
  color: #141021;
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .custom-dialog-title__text {
  color: #e5e7eb;
}

.custom-dialog-title__subtitle {
  min-width: 0;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .custom-dialog-title__subtitle {
  color: rgba(226, 232, 240, 0.6);
}

/* ---- Body ---- */
.custom-dialog-body {
  padding: 16px 16px 24px !important;
  color: #334155;
}

.theme--dark .custom-dialog-body {
  color: rgba(226, 232, 240, 0.9);
}

.custom-dialog-body--no-padding-top {
  padding-top: 4px !important;
}

.custom-dialog-body--no-footer {
  padding-bottom: 28px !important;
}

/* ---- Footer actions (right-aligned, like the products page toolbar) ---- */
.custom-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 28px 28px !important;
  background: transparent;
}

.custom-dialog-action {
  min-height: 48px;
  min-width: 120px !important;
  border-radius: 16px !important;
  padding-right: 12px !important;
  padding-left: 12px !important;
  font-size: 0.875rem !important;
  font-weight: 900 !important;
  letter-spacing: 0;
  text-transform: none !important;
}

/* Delete — red-tinted (note modal delete) */
.custom-dialog-action--delete {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
  transition: background-color 0.15s ease;
}

.custom-dialog-action--delete:hover {
  background: #fee2e2 !important;
}

/* Ensure the delete label keeps its red tint in any theme */
.custom-dialog-action--delete ::v-deep .v-btn__content {
  color: #dc2626 !important;
}

.theme--dark .custom-dialog-action--delete ::v-deep .v-btn__content {
  color: #f87171 !important;
}

.theme--dark .custom-dialog-action--delete {
  background: rgba(239, 68, 68, 0.16) !important;
  color: #f87171 !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.theme--dark .custom-dialog-action--delete:hover {
  background: rgba(239, 68, 68, 0.24) !important;
}

/* ---- Close button (36px, rounded, slate; note modal close) ---- */
.custom-dialog-close {
  flex: 0 0 auto;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  border-radius: 8px !important;
  color: #64748b !important;
  background: transparent !important;
  transition: background-color 150ms ease, color 150ms ease;
}

.custom-dialog-close:hover {
  color: #334155 !important;
  background: #f1f5f9 !important;
}

.theme--dark .custom-dialog-close {
  color: rgba(226, 232, 240, 0.8) !important;
}

.theme--dark .custom-dialog-close:hover {
  color: #ffffff !important;
  background: rgba(148, 163, 184, 0.12) !important;
}

.custom-dialog-close:focus-visible {
  outline: 2px solid #5b39c9;
  outline-offset: 2px;
}

/* ---- Mobile ---- */
@media (max-width: 600px) {
  .custom-dialog-title {
    padding: 20px 16px 0 !important;
  }

  .custom-dialog-body {
    padding: 14px 16px 20px !important;
  }

  .custom-dialog-body--no-padding-top {
    padding-top: 2px !important;
  }

  .custom-dialog-body--no-footer {
    padding-bottom: 20px !important;
  }

  .custom-dialog-actions {
    gap: 6px;
    padding: 14px 16px 20px !important;
  }

  .custom-dialog-action {
    padding-right: 6px !important;
    padding-left: 6px !important;
    font-size: 0.8125rem !important;
  }
}
</style>
